import 'babel-polyfill';
import 'whatwg-fetch';
//@ts-ignore
import * as toBlob from 'canvas-to-blob';
import { DefineModule, inject } from 'dependable-react';
import 'focus-visible';
import * as React from 'react';
import Bugsnag from '@bugsnag/js';
import BugsnagPluginReact from '@bugsnag/plugin-react';
import ReactDOM from 'react-dom';
import TagManager from 'react-gtm-module';
import 'react-image-crop/dist/ReactCrop.css';

import { App } from './containers/App';
import { Page } from './enums/Page';
import './index.css';
import { ApiClient } from './services/ApiClient';
import { BookService } from './services/BookService';
import {
  API_CLEINT_TOKEN,
  APP_DATA_TOKEN,
  BOOK_SERVICE_TOKEN,
  DEAPIFY_TOKEN,
  I18N_TOKEN,
  STORAGE_SERVICE_TOKEN,
  TRANSLATIONS_SERVICE_TOKEN,
} from './services/InjectTokens';
import { StorageService } from './services/StorageService';
import { TranslationsService } from './services/TranslationsService';
import { AppData } from './state/AppData';
import { deapify } from './utils/apify';
import { I18n } from './utils/i18n';

import {
  GTM_ID_PRODUCTION_HR,
  GTM_ID_PRODUCTION_DE,
  GTM_ID_PRODUCTION_SRB,
  GTM_ID_PRODUCTION_PL,
  GTM_ID_DEVELOPMENT,
} from './consts/gtm';

toBlob.init();
// FastClick.attach(document.body);

DefineModule([
  {
    initValue: deapify,
    provider: DEAPIFY_TOKEN,
  },
  {
    initClass: ApiClient,
    provider: API_CLEINT_TOKEN,
  },
  {
    initValue: new AppData(),
    provider: APP_DATA_TOKEN,
  },
  {
    initClass: I18n,
    provider: I18N_TOKEN,
  },
  {
    initClass: BookService,
    provider: BOOK_SERVICE_TOKEN,
  },
  {
    initClass: TranslationsService,
    provider: TRANSLATIONS_SERVICE_TOKEN,
  },
  {
    initClass: StorageService,
    provider: STORAGE_SERVICE_TOKEN,
  },
]);

interface IBoot {
  bookId: number;
  orderItemId: number;
  language?: string;
  el?: HTMLElement;
  token?: string;
  page?: Page;
  skipProducts?: boolean;
  childGender?: string;
  childName?: string;
  extraProducts?: Array<any>;
  wrap?: number | null;
}

async function boot({
  bookId,
  orderItemId,
  el,
  page = Page.PERSONALIZATION,
  language = 'hr',
  token = '',
  skipProducts = false,
  childGender,
  childName,
}: IBoot) {
  return new Promise((resolve) => {
    const bookService = inject(BOOK_SERVICE_TOKEN);
    const state = inject(APP_DATA_TOKEN);
    const storageService = inject(STORAGE_SERVICE_TOKEN);
    const i18n = inject(I18N_TOKEN);

    state.currentLanguage = language === 'sr' ? 'srCyrl' : language;
    state.currentAppLanguage = language;
    state.orderId = orderItemId;
    state.bookId = bookId;
    state.token = token;
    state.childGender = childGender;
    state.childName = childName;

    if (process.env.NODE_ENV === 'development') {
      const dummyDedication = [
        {
          title: 'Some Title 1',
          description: 'Sint sit aliqua aliqua labore.',
        },
        {
          title: 'Some Title 2',
          description: 'Sint sit aliqua aliqua labore.',
        },
        {
          title: 'Some title 3',
          description:
            'Veniam sunt magna aliquip veniam sint occaecat aliquip sit tempor eu sit. Mollit elit dolor enim ut proident culpa veniam.',
        },
      ];

      state.dedications = dummyDedication;
    }

    function setGtmId() {
      let gtmContainer: string;
      if (process.env.NODE_ENV === 'production') {
        switch (language) {
          case 'hr':
            gtmContainer = `${GTM_ID_PRODUCTION_HR}`;
            break;
          case 'de':
            gtmContainer = `${GTM_ID_PRODUCTION_DE}`;
            break;
          case 'pl':
            gtmContainer = `${GTM_ID_PRODUCTION_PL}`;
            break;
          case 'sr' || 'srCyrl':
            gtmContainer = `${GTM_ID_PRODUCTION_SRB}`;
            break;
          default:
            gtmContainer = '';
            break;
        }
        return gtmContainer;
      } else {
        return `${GTM_ID_DEVELOPMENT}`;
      }
    }

    const tagManagerArgs = {
      gtmId: setGtmId(),
    };

    if (tagManagerArgs && tagManagerArgs.gtmId) {
      TagManager.initialize(tagManagerArgs);
    }

    const bookLoader = bookService
      .load(bookId, { childGender })
      //@ts-ignore
      .then(async () => storageService.initialFill(bookId, orderItemId, childName, childGender))
      .then(() => storageService.setupSave(bookId, orderItemId));

    bookService.preloadPages();

    if (process.env.NODE_ENV === 'development') {
      // @ts-ignore
      window['clearBook'] = (oId, bId) => {
        storageService.removeBook(oId || orderItemId, bId || bookId);
        window.location.reload();
      };

      // @ts-ignore
      window['fillAvatars'] = () => {
        state.avatars.forEach((avatar) => {
          avatar.fields.forEach((field) => {
            avatar.params = {
              ...avatar.params,
              [field.uid]: 'asd',
            };
          });
        });
      };
    }

    // @ts-ignore
    window['notifyMissingTranslations'] = i18n.notifyMissingTranslations;

    Bugsnag.start({
      apiKey: '3ad435fc7a202eaaaa2ef37c36df4c4a',
      plugins: [new BugsnagPluginReact()],
    });

    // @ts-ignore
    const ErrorBoundary = Bugsnag.getPlugin('react').createErrorBoundary(React);

    ReactDOM.render(
      // @ts-ignore
      <ErrorBoundary>
        <App initialPage={page} bookLoader={bookLoader} skipProducts={skipProducts} />
      </ErrorBoundary>,
      el ? el : document.getElementById('root'),
      resolve,
    );
  });
}

// @ts-ignore
window['boot'] = boot;
// @ts-ignore
window['appData'] = inject(APP_DATA_TOKEN);

if (process.env.NODE_ENV === 'development') {
  boot({
    bookId: 57,
    childGender: 'f',
    childName: 'Testing',
    language: 'hr',
    orderItemId: 1707,
    page: Page.PERSONALIZATION,
    skipProducts: false,
    token: 'SNZ7vofShXb30D567uwHoPd4p1ug/+rYBe1dQ0yL5tNXrn9Gjcwz2mIgOLSQRKYLK21onaRUj2BIU7bSP5NGuw==',
  })
    // tslint:disable-next-line: no-console
    .then(() => console.log('booted in development mode'))
    // tslint:disable-next-line: no-console
    .catch(() => console.error('could not boot'));
}
